import { motion } from 'framer-motion';
import { 
  Euro, 
  Clock, 
  AlertTriangle, 
  Users, 
  Briefcase,
  FileText,
  TrendingDown,
  CheckCircle,
  ArrowRight,
  Building,
  Calculator,
  Search,
  Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const hiddenCosts = [
  { icon: Building, label: "Bureau ou espace de travail" },
  { icon: Calculator, label: "Ordinateur et matériel" },
  { icon: FileText, label: "Logiciels professionnels" },
  { icon: Clock, label: "Temps de formation" },
  { icon: Users, label: "Temps de management" },
];

const recruitmentSteps = [
  "Rédaction de l'offre",
  "Tri des candidatures",
  "Entretiens",
  "Période d'essai",
  "Formation",
];

export default function SalaryCostPage() {
  return (
    <div data-testid="salary-cost-page">
      {/* Hero */}
      <section className="py-20 md:py-32 gradient-mesh">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-2 bg-orange-100 text-orange-600 text-sm font-medium rounded-full mb-4">
              Comprendre les coûts
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-rm-text mb-6">
              Pourquoi recruter un(e) assistant(e) peut coûter{' '}
              <span className="text-gradient">plus de 50 000 € par an</span>
            </h1>
            <p className="text-lg md:text-xl text-rm-text-secondary max-w-3xl mx-auto mb-4">
              Beaucoup d'entrepreneurs pensent qu'embaucher un(e) assistant(e) de direction est la solution pour mieux s'organiser.
            </p>
            <p className="text-lg md:text-xl text-rm-text-secondary max-w-3xl mx-auto">
              Mais le <strong className="text-rm-text">coût réel d'un salarié</strong> est souvent bien plus élevé que le salaire affiché.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 2 - Le salaire n'est qu'une partie */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                <Euro className="w-8 h-8 text-orange-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
                Le salaire n'est qu'une partie du coût
              </h2>
              <p className="text-lg text-rm-text-secondary mb-6">
                Prenons un exemple simple.
              </p>
              <p className="text-rm-text-secondary mb-4">
                Un(e) assistant(e) de direction avec un salaire d'environ <strong className="text-rm-text">2 000 € net par mois</strong> correspond généralement à :
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-orange-500">•</span>
                  <span className="text-rm-text-secondary">environ <strong className="text-rm-text">2 600 € à 2 800 € brut</strong></span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-orange-500">•</span>
                  <span className="text-rm-text-secondary">environ <strong className="text-rm-text">3 500 € à 4 000 € de coût total</strong> pour l'entreprise</span>
                </div>
              </div>
              <p className="text-rm-text font-medium">
                Cela représente déjà :
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-3xl p-8 text-center"
            >
              <p className="text-6xl md:text-7xl font-bold text-orange-600 mb-4">42 000 €</p>
              <p className="text-2xl text-orange-700 font-medium">à 48 000 € par an</p>
              <p className="text-orange-600 mt-4">Rien que pour le salaire et les charges</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 3 - Coûts cachés */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-4">
              Les coûts souvent oubliés
            </h2>
            <p className="text-lg text-rm-text-secondary max-w-3xl mx-auto">
              Mais le salaire n'est pas le seul coût. Lorsque tu recrutez une personne, tu dois également prévoir :
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {hiddenCosts.map((cost, index) => {
              const Icon = cost.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 text-center border border-slate-100 hover:shadow-lg transition-all"
                >
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-orange-600" />
                  </div>
                  <p className="text-rm-text font-medium text-sm">{cost.label}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-rm-text mt-8 font-medium"
          >
            Ces éléments peuvent facilement représenter <strong>plusieurs milliers d'euros supplémentaires</strong> chaque année.
          </motion.p>
        </div>
      </section>

      {/* Section 4 - Coût réel > 50 000 € */}
      <section className="py-20 bg-orange-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
              Le coût réel peut dépasser 50 000 € par an
            </h2>
            <p className="text-lg text-rm-text-secondary mb-8">
              Un(e) assistant(e) de direction peut donc représenter un coût réel supérieur à :
            </p>
            <div className="bg-white rounded-3xl p-8 shadow-lg inline-block mb-8">
              <p className="text-6xl md:text-7xl font-bold text-orange-600 mb-2">50 000 €+</p>
              <p className="text-xl text-rm-text-secondary">par an pour l'entreprise</p>
            </div>
            <p className="text-rm-text-secondary mb-4">Et cela sans compter :</p>
            <div className="flex flex-wrap justify-center gap-3">
              <span className="bg-white px-4 py-2 rounded-full text-rm-text-secondary">le temps nécessaire pour recruter</span>
              <span className="bg-white px-4 py-2 rounded-full text-rm-text-secondary">le risque d'erreur de recrutement</span>
              <span className="bg-white px-4 py-2 rounded-full text-rm-text-secondary">les contraintes administratives</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 5 - Temps de recrutement */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-rm-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Clock className="w-8 h-8 text-rm-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
                Le temps nécessaire pour recruter
              </h2>
              <p className="text-lg text-rm-text-secondary mb-6">
                Recruter une personne demande du temps :
              </p>
              <div className="space-y-3 mb-6">
                {recruitmentSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-8 h-8 bg-rm-primary/10 rounded-lg flex items-center justify-center text-sm font-bold text-rm-primary">
                      {index + 1}
                    </div>
                    <span className="text-rm-text-secondary">{step}</span>
                  </motion.div>
                ))}
              </div>
              <p className="text-rm-text font-medium">
                Pendant plusieurs mois, l'entrepreneur doit souvent investir beaucoup d'énergie pour rendre la personne opérationnelle.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-rm-primary/5 to-rm-accent/10 rounded-3xl p-8 flex items-center justify-center"
            >
              <div className="text-center">
                <Search className="w-20 h-20 text-rm-primary mx-auto mb-4" />
                <p className="text-xl font-semibold text-rm-text">Processus long et complexe</p>
                <p className="text-rm-text-secondary">Plusieurs mois avant d'être opérationnel</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 6 - Risque d'erreur */}
      <section className="py-20 bg-red-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
              Le risque d'erreur de recrutement
            </h2>
            <p className="text-lg text-rm-text-secondary mb-6">
              Le recrutement reste toujours une décision importante.
            </p>
            <p className="text-rm-text-secondary mb-4">
              Si la personne ne correspond pas au poste :
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <TrendingDown className="w-8 h-8 text-red-500 mx-auto mb-3" />
                <p className="text-rm-text font-medium">L'entreprise perd du temps</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <Euro className="w-8 h-8 text-red-500 mx-auto mb-3" />
                <p className="text-rm-text font-medium">L'entreprise perd de l'argent</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <Users className="w-8 h-8 text-red-500 mx-auto mb-3" />
                <p className="text-rm-text font-medium">L'organisation est perturbée</p>
              </div>
            </div>
            <p className="text-rm-text font-medium">
              Mettre fin à un contrat peut également être <strong>complexe et coûteux</strong> pour une petite entreprise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 7 - Comparaison horaires France vs Madagascar */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-rm-primary/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <Calendar className="w-8 h-8 text-rm-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-4">
              Une différence importante à connaître
            </h2>
            <p className="text-lg text-rm-text-secondary max-w-3xl mx-auto">
              Le coût mentionné ci-dessus correspond à un contrat français standard.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* France */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-100 rounded-3xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl">🇫🇷</span>
                <h3 className="text-xl font-bold text-rm-text">En France</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <span className="text-rm-text-secondary">Temps de travail</span>
                  <span className="font-bold text-rm-text">35h / semaine</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <span className="text-rm-text-secondary">Congés payés</span>
                  <span className="font-bold text-rm-text">5 semaines</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <span className="text-rm-text-secondary">Coût annuel</span>
                  <span className="font-bold text-orange-600">&gt; 50 000 €</span>
                </div>
              </div>
            </motion.div>

            {/* Madagascar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-rm-primary to-rm-primary/80 rounded-3xl p-8 text-white"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl">🇲🇬</span>
                <h3 className="text-xl font-bold">Avec BatiAssist (Madagascar)</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white/10 rounded-lg">
                  <span className="text-white/80">Temps de travail</span>
                  <span className="font-bold">~40h / semaine</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/10 rounded-lg">
                  <span className="text-white/80">Congés payés</span>
                  <span className="font-bold">30 jours / an</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/10 rounded-lg">
                  <span className="text-white/80">Coût annuel</span>
                  <span className="font-bold text-green-300">À partir de 11 388 €</span>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-rm-text-secondary mt-8"
          >
            <strong className="text-rm-text">Plus d'heures de travail, même nombre de congés, et un coût 4 à 5 fois inférieur.</strong>
          </motion.p>
        </div>
      </section>

      {/* Section 8 - Une autre approche */}
      <section className="py-20 bg-rm-primary/5">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6 text-center">
              Une autre approche pour organiser son entreprise
            </h2>
            <p className="text-lg text-rm-text-secondary mb-8 text-center">
              C'est pour cette raison que de nombreux entrepreneurs cherchent aujourd'hui des solutions plus flexibles pour structurer leur organisation.
            </p>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <p className="text-xl font-semibold text-rm-text mb-6 text-center">
                Avec <span className="text-rm-primary">BatiAssist</span>, tu bénéficies :
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-rm-text">d'un Super Assistant à mi-temps ou plein temps</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-rm-text">d'environ 40 heures par semaine</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-rm-text">d'une équipe digitale complète</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-rm-text">d'outils d'automatisation</span>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 mb-6">
                <div className="flex flex-wrap justify-center gap-4 text-sm text-rm-text-secondary">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Sans charges sociales
                  </span>
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Sans gestion RH
                  </span>
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Sans contraintes administratives
                  </span>
                </div>
              </div>

              <div className="text-center">
                <p className="text-sm text-rm-text-secondary mb-2">Une organisation professionnelle à partir de</p>
                <p className="text-4xl font-bold text-rm-primary mb-1">949 € <span className="text-lg font-normal text-rm-text-secondary">/mois</span></p>
                <p className="text-sm text-rm-text-secondary">sans engagement</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 9 - Conclusion */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
              Pour une petite entreprise, c'est un engagement financier important
            </h2>
            <p className="text-lg text-rm-text-secondary mb-4">
              Recruter un(e) assistant(e) de direction en France représente un investissement conséquent.
            </p>
            <p className="text-lg text-rm-text-secondary">
              De nombreux entrepreneurs choisissent aujourd'hui des <strong className="text-rm-text">solutions plus flexibles</strong>, qui permettent d'organiser l'entreprise sans prendre de risque financier trop important.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-rm-primary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Découvre comment structurer ton entreprise autrement
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Réserve un entretien gratuit avec Romain pour discuter de ton projet.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-white hover:bg-white/90 text-rm-primary font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
              >
                Réserver un entretien avec Romain
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
